import { useEffect, useState } from 'react';

import InputField from '../common/components/InputField.tsx';
import Checkbox from '../common/components/Checkbox.tsx';
import DriverMap from './DriverMap.jsx';
import AlertModal from './AlertModal.jsx';
import AlertViewModel from '../js/viewmodels/AlertViewModel.js';
import { DateTime } from "luxon";

import XmlBlock from './XmlBlock.jsx';

import '../css/DriverLogsList.css';

export default function DriverLogsList({
  model
}) {
  const [showMap, setShowMap] = useState(false)

  const modelAlert = AlertViewModel();

  function submit() {

    const start = DateTime.fromISO(model.startDate);
    const end = DateTime.fromISO(model.endDate);

    var hasDeviceId = model.deviceId.length > 0;
    var hasDriverNo = model.driverNo.length > 0;
    var hasCompany = model.companyCode.length > 0;

    if (hasDeviceId && (hasDriverNo || hasCompany)) {
      modelAlert.show('Enter device id OR driver number + company', 'Error');
      return;
    }

    if (!hasDeviceId && !(hasDriverNo && hasCompany)) {
      modelAlert.show('Enter device id OR driver number + company', 'Error');
      return;
    }

    if (hasDriverNo !== hasCompany) {
      modelAlert.show('Enter driver number AND company, not just one', 'Error');
      return;
    }

    if (!start.isValid || !end.isValid || start > end) {
      modelAlert.show('Invalid date/time range', 'Error');
      return;
    }

    model.search();
  }

  function cancel() {
    //TODO ajax request
    // model.setRequestInProgress(false);
    model.cancelSearch();
  }

  const submitButton = model.requestInProgress ?
    (<button className="btn btn-danger" type="button" onClick={cancel}>Cancel</button>) :
    (<>
      <button className="btn btn-success" type="button" onClick={submit}>Submit</button>
      {model.logs.length > 0 && (
        <button className="btn btn-secondary ms-2" type="button" onClick={() => model.clearResults()}>Clear</button>
      )}
    </>);

  const isDesiredEventType = eventType => (
    (eventType === 'gps' && model.includeGps)
    || (eventType === 'message' && model.includeMessaging)
    || (eventType === 'job' && model.includeJobEvents)
    || (eventType === 'login' && model.includeLogin)
    || (eventType === 'other' && model.includeOther)
  )

  // We only want to show logs which contain at least one of the desired event types.
  const logsToDisplay = model.logs.filter(log =>
    log.events.some(event => isDesiredEventType(event.type))
  )

  return (
    <div className="DriverLogsList">
      <div className="container">
        <div className="row">
          <div className="col l2 s6 input-field">
            <InputField
              label="Device ID"
              value={model.deviceId}
              setValue={model.setDeviceId}
              onEnter={submit}
              autoFocus={true}
            />
          </div>
          <div className="col l2 s6 input-field">
            <InputField
              label="Driver Number"
              value={model.driverNo}
              setValue={model.setDriverNo}
              onEnter={submit}
            />
          </div>
          <div className="col l2 s6 input-field">
            <InputField
              label="Company Code"
              value={model.companyCode}
              setValue={model.setCompanyCode}
              onEnter={submit}
            />
          </div>
        </div>
        <div className="row">

          <div className="col l2 s6 input-field">
            <InputField
              label="Start"
              type="datetime-local"
              value={model.startDate}
              setValue={model.setStartDate}
              onEnter={submit}
            />
          </div>
          <div className="col l2 s6 input-field">
            <InputField
              label="End"
              type="datetime-local"
              value={model.endDate}
              setValue={model.setEndDate}
              onEnter={submit}
            />
          </div>
          <div className="col l2 s6 input-field">
            <div className="mb-3">
              <br />
              {submitButton}
            </div>
          </div>
        </div>

        <br />

        <div className="row align-items-center">
          <div className="col-auto">
            <Checkbox
              id="gps"
              label="GPS"
              value={model.includeGps}
              setValue={model.setIncludeGps}
            />
          </div>
          <div className="col-auto">
            <Checkbox
              id="messaging"
              label="Messaging"
              value={model.includeMessaging}
              setValue={model.setIncludeMessaging}
            />
          </div>
          <div className="col-auto">
            <Checkbox
              id="jobevents"
              label="Job Events"
              value={model.includeJobEvents}
              setValue={model.setIncludeJobEvents}
            />
          </div>
          <div className="col-auto">
            <Checkbox
              id="login"
              label="Login"
              value={model.includeLogin}
              setValue={model.setIncludeLogin}
            />
          </div>
          <div className="col-auto">
            <Checkbox
              id="other"
              label="Other"
              value={model.includeOther}
              setValue={model.setIncludeOther}
            />
          </div>
          <div className="col-auto flex-grow-1">
            <button className="btn btn-primary float-end" type="button" onClick={() => setShowMap(!showMap)}>
              Toggle Map
            </button>
          </div>
        </div>
      </div>

      <br />

      <div className={showMap ? 'tableMapWrapper' : 'container-lg'}>
        <DriverLogTable
          logs={logsToDisplay}
          setMapMarker={(marker) => {
            setShowMap(true)
            model.setMapMarker(marker)
          }}
          isDesiredEventType={isDesiredEventType}
          xmlSearch={model.xmlSearch}
          setXmlSearch={model.setXmlSearch}
        />

        {showMap && <DriverMap
          marker={model.mapMarker}
          coords={model.gpsTrail}
          apiKey={model.apiKey}
        />}
      </div>

      <AlertModal model={modelAlert} />
    </div>
  )

}

const DriverLogTable = ({
  logs,
  isDesiredEventType,
  setMapMarker,
  xmlSearch,
  setXmlSearch
}) => {
  return <div className="DriverLogTable">
    <div className="grid header">
      <div className="gridRow">
        <span>Timestamp</span>
        <span>Origin</span>
        <span>
          Event
          <InputField
            placeholder="Search"
            className="xmlSearch"
            setValue={setXmlSearch}
            outerClassName=""
          />
        </span>
      </div>
    </div>
    <div className="grid body">
      {logs.map(log => <TableRow
        log={log}
        isDesiredEventType={isDesiredEventType}
        setMapMarker={setMapMarker}
        xmlSearch={xmlSearch}
      />)}
    </div>
  </div>
}

function TableRow({
  log,
  isDesiredEventType,
  setMapMarker,
  xmlSearch,
}) {
  const [showRow, setShowRow] = useState(true)

  useEffect(() => {
    if (!xmlSearch) {
      if (!showRow) setShowRow(true)
      return
    }

    setShowRow(log.xml.toLowerCase().includes(xmlSearch))
  }, [log, xmlSearch])

  if (!showRow) return undefined

  const lines = log.events.map((event, index) => <TableRowText key={index}>
    {event.text}
  </TableRowText>);

  let showOnMap = undefined
  if (log.coord) {
    showOnMap = () => {
      // Cheeky destructure to make sure the object changes each time. ;)
      setMapMarker({ ...log.coord })
    }
  }

  return <div className="gridRow">
    <span>
      {log.stamp && <span>{log.stamp} <span className="text-muted" style={{ fontSize: '0.8em' }}>Device</span></span>}
      {log.serverStamp && <><br /><span className="text-muted" style={{ fontSize: '0.8em' }}>{log.serverStamp} Server</span></>}
    </span>
    <span>{log.origin}</span>
    <span>
      <div className="lines">
        <ul>
          {log.events.map((event, index) => <li key={index} className={isDesiredEventType(event.type) ? '' : 'irrelevant'}>
            {event.text}
          </li>)}
        </ul>
      </div>
      {log.xml && <XmlBlock xml={log.xml} showOnMap={showOnMap} searchTerm={xmlSearch} />}
    </span >
  </div >
}

function TableRowText({
  children,
}) {
  return <span>
    {children}
    <br />
  </span>;
}
